import { useSuspenseQuery } from "@apollo/client";
import {
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { Details } from "~/components/TransferDetail/Details";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/transfers_/$transferId")({
  head: () => opsHead("Transfer"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transferId } = Route.useParams();

  const { data: gqlData, error } = useSuspenseQuery(
    graphql(
      `query TransferDetailPage($id: ID!) {
        node (id: $id) {
          id
          ... on Transfer {
            id
            paymentReference
            ledgerTransactions {
              id
              ...RelatedLedgerTransactions
            }
          }
          ...TransferDetailPage_Details
        }
      }`,
    ),
    {
      variables: { id: transferId },
    },
  );

  if (!gqlData || gqlData.node?.__typename !== "Transfer") {
    throw new NotFoundError();
  }

  const transfer = gqlData.node;

  useOpsTitle(`Transfer #${transfer.paymentReference}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Transfer #${transfer.paymentReference}`}
        subtitle={<div className="font-mono">{transfer.id}</div>}
      />
      <Tabs defaultValue="details">
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <Details transfer={transfer} error={error} />
        </TabsContent>
        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={transfer.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
